import React from "react";
import { Divider, Button, Empty, Spin } from "antd";
import { useOrderByIdQuery } from "../../Services";
import { OrderOverview } from "./OrderDetailsDrawer/OrderOverview";
import { OrderItemsTable } from "./OrderDetailsDrawer/OrderItemsTable";
import { OrderPaymentsTable } from "./OrderDetailsDrawer/OrderPaymentsTable";
import { OrderReturnsSection } from "./OrderDetailsDrawer/OrderReturnsSection";
import { drawerTitle } from "../AppUi";
import { ResizableDrawer } from "../ResizableDrawer/ResizableDrawer";

interface OrderDetailsDrawerProps {
  open: boolean;
  onClose: () => void;
  orderId: string | null;
  onViewHistory: () => void;
}

export const OrderDetailsDrawer: React.FC<OrderDetailsDrawerProps> = ({
  open,
  onClose,
  orderId,
  onViewHistory,
}) => {
  const { data: order, isFetching: loading } = useOrderByIdQuery(
    orderId || "",
    { enabled: open && !!orderId },
  );

  const hasHistory =
    (order?.edit_history && order.edit_history.length > 0) ||
    (order?.history_items && (order.history_items as any[]).length > 0) ||
    (order?.history_payments && (order.history_payments as any[]).length > 0);

  const returnedQtyMap: Record<string, number> = {};
  (order?.returns || []).forEach((ret) => {
    (ret.items || []).forEach((ri) => {
      returnedQtyMap[ri.order_item_id] =
        (returnedQtyMap[ri.order_item_id] || 0) + ri.quantity;
    });
  });

  const hasReturns = (order?.returns?.length ?? 0) > 0;

  return (
    <ResizableDrawer
      title={drawerTitle(`Order Details - ${order?.order_no || "Loading..."}`)}
      extra={
        hasHistory && (
          <Button size="middle" onClick={onViewHistory}>
            Edit History
          </Button>
        )
      }
      open={open}
      onClose={onClose}
      width={650}
      placement="right"
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center h-64 gap-2">
          <Spin size="default" />
          <span className="text-xs text-gray-400">
            Fetching order details...
          </span>
        </div>
      ) : order ? (
        <div className="flex flex-col gap-4 text-xs">
          <OrderOverview order={order} />

          <Divider className="my-2" />

          <OrderItemsTable order={order} />

          <Divider className="my-2" />

          <OrderPaymentsTable order={order} />

          <Divider className="my-2" />

          {hasReturns && <OrderReturnsSection order={order} />}
        </div>
      ) : (
        <Empty description="No data loaded" />
      )}
    </ResizableDrawer>
  );
};
